import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ListColumnType, ListFilterFieldType, ListMoreMenuConfig, QueryParamType } from 'types/ListTypes';
import { BulkActionsConfig } from 'types/BulkActionTypes';
import { useFetchData } from 'services/api/useFetchData';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { useAdminOnlyContactTypes } from './contactTypeOptions';
import { useModal } from 'components/modals/ModalContext';
import AbstractList from 'components/lists/AbstractList';
import ContactDuplicatesModal, { ContactDuplicateGroup } from 'components/modals/ContactDuplicatesModal';
import ContactForm from './ContactForm';
import '../../style/scss/contact-merge.scss';

/*
 * ContactListBase.tsx
 * Shared configuration of the contact lists in the contacts menu.
 * Every list (clients, suppliers, leads, organizations and persons)
 * fixes a contact type or entity type through the default params and
 * presets the same value in the add form, so a contact added from a
 * list always lands in that list.
 */

interface ContactListBaseProps {
    listKey: string; // The bulk action and column preference key of the list
    objectName: string; // The translation prefix of the object name used in the add button
    defaultParams?: QueryParamType; // The params fixing the contact type or entity type of the list
    formLinkedItem?: { [key: string]: string }; // The values preset and hidden in the add form
    showTypeFilter?: boolean; // Whether the contact type filter is shown
    typeFilterOptions?: string[]; // The contact types offered in the type filter
    showEntityTypeFilter?: boolean; // Whether the entity type filter is shown
    showNumberColumn?: boolean; // Whether the customer number column is shown by default
}

const ContactListBase: React.FC<ContactListBaseProps> = ({
    listKey, objectName, defaultParams = {}, formLinkedItem, showTypeFilter = false,
    typeFilterOptions = ['contact', 'lead', 'client', 'supplier'], showEntityTypeFilter = false, showNumberColumn = false,
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { initializeModal } = useModal();

    // Merging soft deletes contacts, so it follows the delete right
    const canMerge = useAllowedRight('can_delete');

    // The cancelled type only exists in the admin environment, it joins the type filter there
    const adminOnlyTypes = useAdminOnlyContactTypes();
    const visibleTypeFilterOptions = [...typeFilterOptions, ...adminOnlyTypes.filter(type => !typeFilterOptions.includes(type))];

    // Fetch the possible duplicate contacts within the scope of this list
    const duplicateParams = useMemo(() => ({ ...defaultParams }), [JSON.stringify(defaultParams)]);
    const { response: duplicatesResponse } = useFetchData({ apiUrl: 'get_contact_duplicates', params: duplicateParams });
    const duplicateGroups: ContactDuplicateGroup[] = duplicatesResponse?.groups ?? [];
    const duplicateCount: number = duplicatesResponse?.count ?? 0;

    // Open the merge preview page for the given contact ids, the first id is the main contact
    const openMergePage = (ids: number[]) => {
        history.push(`/contacts/merge?ids=${ids.join(',')}&target=${ids[0]}`);
    };

    // Open the modal listing the possible duplicate groups
    const openDuplicatesModal = () => {
        initializeModal(
            <ContactDuplicatesModal groups={duplicateGroups} truncated={duplicatesResponse?.truncated} onMerge={openMergePage} />,
            { title: t('contact.merge.duplicates_modal_title'), modalSize: 'small' }
        );
    };

    // Render the duplicates notice line above the list
    const renderNotice = () => {
        if (!canMerge || duplicateCount === 0) return null;
        return (
            <span className='list-notice-text'>
                {t(duplicateCount === 1 ? 'contact.merge.duplicates_notice' : 'contact.merge.duplicates_notice_plural', { count: duplicateCount })}
                <span className='list-notice-link' onClick={openDuplicatesModal}>
                    {t('contact.merge.duplicates_notice_link')}
                </span>
            </span>
        );
    };

    // Enable the bulk actions panel for this list, the columns share the contact translations
    const contactBulkActions: BulkActionsConfig = {
        objectKey: listKey,
        translationKey: 'contact',
        deleteOrDeactivate: 'delete',
        showExport: true,
        customActions: canMerge ? [{
            key: 'merge',
            label: 'button.merge_button_label',
            onSelect: openMergePage,
            minSelection: 2,
            maxSelection: 10,
            disabledMessage: 'contact.merge.bulk_selection_hint',
        }] : [],
    };

    // Configure the more menu of this list, the fixed params stay active while showing deleted contacts
    const contactMoreMenu: ListMoreMenuConfig = {
        importObject: 'contact',
        inactiveItemsToggle: {
            semantic: 'deleted',
            activeParams: { ...defaultParams, deleted: true },
            clearFilterNames: showTypeFilter ? ['contact_type_filter'] : [],
        },
    };

    // Specify the options of this list, the default params fix the contact type or entity type
    const query = {
        endpoint: 'get_contact_list',
        object: 'contact',
        defaultParams,
    }

    // Set isPaginated to true if this list may have 100.000+ items
    const isPaginated = true

    // Specify form, detail modal or detailpage for this list, the linked item presets the add form
    const formModal = <ContactForm linkedItem={formLinkedItem} />;
    const modalSize = 'add-form';
    const onRowClick = 'detailPage'
    const detailPageUrl = {
        baseUrl: 'contacts',
        urlSuffixApiField: 'entity_type',
        urlSuffixFallback: 'contact'
    };

    // Custom jsx to render the labels as badges
    const renderLabelBadges = (labels: { id: number, name: string, color: string }[] = []) => {
        return (
            <div className='badge-container'>
                {labels.map(label => (
                    <div key={label.id} className={`label-badge ${label.color}`}>
                        {label.name}
                    </div>
                ))}
            </div>
        );
    }

    // Specify the columns of this list
    const columns: ListColumnType[] = [
        ...(showNumberColumn ? [{
            field: 'number',
            label: 'contact.general.number_label',
            width: '0.5fr'
        }] : []),
        { 
            field: 'name_and_address', 
            orderField: 'name',
            label: 'contact.general.name_label', 
            width: '2fr' 
        },
        { 
            field: 'labels', 
            label: 'contact.general.labels_label', 
            disableOrdering: true,
            width: '1fr', render: renderLabelBadges 
        }
    ];

    // Specify the filter bar fields of this list, a fixed type or entity type has no filter
    const filterFields: ListFilterFieldType[] = useMemo(() => [
        {
            type: 'search',
            name: 'search',
            placeholder: 'forms.search_placeholder',
            queryParam: 'search',
            width: 1.5
        },
        ...(showTypeFilter ? [{
            type: 'dropdown' as const,
            name: 'contact_type_filter',
            label: 'contact.general.contact_type_label',
            queryParam: 'contact_type_filter',
            backendField: 'id',
            optionField: 'id',
            translationPrefix: 'contact.contact_type.',
            hardcodedOptions: visibleTypeFilterOptions.map(contactType => ({ id: contactType })),
            width: 1.25
        }] : []),
        ...(showEntityTypeFilter ? [{
            type: 'dropdown' as const,
            name: 'entity_type',
            label: 'contact.general.entity_type_label',
            queryParam: 'entity_type',
            backendField: 'id',
            optionField: 'label',
            translationPrefix: 'contact.entity_type.',
            hardcodedOptions: [
                { id: 'organization', label: 'organizations' },
                { id: 'person', label: 'persons' },
            ],
            width: 1.25
        }] : []),
        {
            type: 'multiselect',
            name: 'labels',
            label: 'contact.general.labels_label',
            queryParam: 'labels__in',
            apiRequest: {
                endpoint: 'get_label_for_feature_list/contacts',
                object: 'contact_label',
            },
            width: 1.5
        }
    ], [showTypeFilter, showEntityTypeFilter, visibleTypeFilterOptions.join(',')]);

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            columns={columns}
            filterFields={filterFields}
            bulkActions={contactBulkActions}
            moreMenu={contactMoreMenu}
            renderNotice={renderNotice}
            formModal={formModal}
            modalSize={modalSize}
            isPaginated={isPaginated}
            onRowClick={onRowClick}
            detailPageUrl={detailPageUrl}
        />
    );
};

export default ContactListBase;
